import { useState, useEffect, useCallback } from 'react';
import { useBottomSheetInternal } from '@gorhom/bottom-sheet';
import { SelectableOptionItem } from '../../Select/Select.types';

export function useInputSelect(
  selectableOptions: SelectableOptionItem[],
  dynamicSelectableOptionsCallback?: (
    ...args: unknown[]
  ) => SelectableOptionItem[] | Promise<SelectableOptionItem[]>,
  useDynamicSelectableOptions = false,
  defaultValue?: string,
) {
  const [inputValue, setInputValue] = useState(defaultValue || '');
  const [filteredSuggestions, setFilteredSuggestions] =
    useState<SelectableOptionItem[]>(selectableOptions);
  const { shouldHandleKeyboardEvents } = useBottomSheetInternal();

  const handleInputChange = useCallback(
    async (value: string) => {
      setInputValue(value);

      if (!useDynamicSelectableOptions) {
        if (value && selectableOptions.length > 0) {
          const filtered = selectableOptions.filter((option) =>
            option.text.toLowerCase().includes(value.toLowerCase()),
          );
          setFilteredSuggestions(filtered);
        } else {
          setFilteredSuggestions(selectableOptions);
        }
      } else {
        const dynamicSelectableOptions =
          await dynamicSelectableOptionsCallback?.(value);

        if (dynamicSelectableOptions) {
          setFilteredSuggestions(dynamicSelectableOptions);
        }
      }
    },
    [
      selectableOptions,
      useDynamicSelectableOptions,
      dynamicSelectableOptionsCallback,
    ],
  );

  const handleOnFocus = useCallback(() => {
    shouldHandleKeyboardEvents.value = true;
  }, [shouldHandleKeyboardEvents]);

  const handleOnBlur = useCallback(() => {
    shouldHandleKeyboardEvents.value = false;
  }, [shouldHandleKeyboardEvents]);

  const handleOnPress = useCallback(
    (item: SelectableOptionItem | null) => {
      setInputValue(item?.text || '');
    },
    [setInputValue],
  );

  useEffect(() => {
    shouldHandleKeyboardEvents.value = true;

    return () => {
      shouldHandleKeyboardEvents.value = false;
    };
  }, [shouldHandleKeyboardEvents]);

  return {
    inputValue,
    filteredSuggestions,
    setFilteredSuggestions,
    setInputValue,
    handleOnPress,
    handleInputChange,
    handleOnFocus,
    handleOnBlur,
  };
}
